// Sidebar — navigation KNOWROK : sections groupées + barre rétractable (mode icônes).
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useModules } from "../../hooks/useModules";
import {
  LayoutDashboard,
  HardHat,
  Map as MapIcon,
  CalendarDays,
  CalendarRange,
  Clock,
  CalendarOff,
  Users,
  StickyNote,
  HardHat as LogoIcon,
  Building2,
  ClipboardList,
  FileText,
  Briefcase,
  Receipt,
  Package,
  Wallet,
  Euro,
  Flag,
  Wrench,
  IdCard,
  UserCheck,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { can } from "../../lib/permissions"; // V2-T8
import { UNIVERSES_ENABLED } from "../../lib/universes";

// ── Design tokens sidebar ───────────────────────────────────────────────────
const S = {
  bg:           "var(--cf-surface)",
  bgLight:      "var(--cf-surface-2)",
  activeBg:     "var(--cf-blue-light)",
  activeText:   "var(--cf-blue-text)",
  inactiveText: "var(--cf-muted)",
  hoverBg:      "var(--cf-bg)",
  separator:    "var(--cf-border)",
  badgeBg:      "var(--cf-blue-mid)",
};

const WORKER_ROLE          = "worker";
const MANAGER_ROLES        = ["owner", "manager"];
const SITES_ROLES          = ["owner", "manager", "foreman"];
const WORKER_FOREMAN_ROLES = ["worker", "foreman"];
const CREATION_ROLES       = ["worker", "foreman", "manager"];

// Sections de navigation (ordre d'affichage).
// Refonte du 16/08 : Demandes remonte dans Pilotage, et la section
// Interventions disparaît de la barre — cet univers a son propre espace,
// atteint par le sélecteur d'univers ; l'afficher ici le dupliquait.
const SECTIONS = [
  { key: "pilotage",    label: "Pilotage" },
  { key: "rh",          label: "RH & heures" },
  // Hors flag univers, l'espace Interventions n'existe pas : la section
  // maintenance reste alors la seule porte d'entrée.
  ...(UNIVERSES_ENABLED ? [] : [{ key: "maintenance", label: "Maintenance" }]),
];

// Entrées mises en sommeil : masquées de la barre, routes et données
// conservées. Réactivables par entreprise via le réglage `nav_visible`.
// Stock a quitte l'application le 16/08/2026 : son entree est retiree, pas
// endormie — il reviendra sous une autre forme. Voir App.js.
const EN_SOMMEIL_PAR_DEFAUT = ["factures_st"];

function buildNav(role) {
  const isWorker = role === WORKER_ROLE;
  const items = [
    // ── Pilotage ──
    { to: "/dashboard",   label: "Tableau de bord",     icon: LayoutDashboard, testid: "nav-dashboard",   show: true,                              section: "pilotage", end: true },
    { to: "/sites",       label: "Chantiers",           icon: HardHat,         testid: "nav-sites",       show: SITES_ROLES.includes(role),        section: "pilotage" },
    { to: "/my-sites",    label: "Mes chantiers",       icon: Building2,       testid: "nav-my-sites",    show: WORKER_FOREMAN_ROLES.includes(role), section: "pilotage" },
    { to: "/sites/map",   label: "Carte des chantiers", icon: MapIcon,         testid: "nav-sites-map",   show: SITES_ROLES.includes(role),        section: "pilotage" },
    { to: "/tasks",       label: "Tâches",              icon: ClipboardList,   testid: "nav-tasks",       show: MANAGER_ROLES.includes(role),      section: "pilotage", isNew: true },
    { to: "/planning",    label: "Planning",            icon: CalendarDays,    testid: "nav-planning",    show: SITES_ROLES.includes(role),        section: "pilotage", module: "module_planning" },
    { to: "/my-schedule", label: "Mon planning",        icon: CalendarRange,   testid: "nav-my-schedule", show: isWorker,                          section: "pilotage", module: "module_planning" },
    // Jalons : le backend (milestones.py EDIT_ROLES) autorise owner, manager et
    // foreman — réserver l'entrée au seul owner privait managers et chefs de
    // chantier d'une page qu'ils ont le droit d'utiliser.
    { to: "/jalons",      label: "Jalons",              icon: Flag,            testid: "nav-jalons",      show: SITES_ROLES.includes(role),        section: "pilotage", module: "module_planning", isNew: true },
    { to: "/memos",       label: "Mémos",               icon: StickyNote,      testid: "nav-memos",       show: SITES_ROLES.includes(role),        section: "pilotage" },
    { to: "/admin/factures",      label: "Factures ST",    icon: Receipt,      testid: "nav-factures-st",   show: MANAGER_ROLES.includes(role),    section: "pilotage", sommeil: "factures_st" },
    // ── RH & heures ──
    // Refonte 16/08 : « RH » (coquille vide) devient le tableau de bord de la
    // section et passe en tête ; Utilisateurs + Intérimaires + Sous-traitants
    // fusionnent en « Mes intervenants ».
    { to: "/rh",          label: "Tableau de bord RH",  icon: IdCard,          testid: "nav-rh",          show: MANAGER_ROLES.includes(role),      section: "rh", module: "module_rh" },
    { to: "/timesheets",  label: "Heures",              icon: Clock,           testid: "nav-timesheets",  show: true,                              section: "rh", module: "module_pointage" },
    { to: "/couts-mo",    label: "Coût main-d'œuvre",   icon: Euro,            testid: "nav-couts-mo",    show: MANAGER_ROLES.includes(role),      section: "rh", module: "module_pointage" },
    { to: "/absences",    label: "Absences",            icon: CalendarOff,     testid: "nav-absences",    show: true,                              section: "rh", module: "module_rh" },
    { to: "/notes-de-frais", label: "Notes de frais",   icon: Wallet,          testid: "nav-expenses",    show: true,                              section: "rh", module: "module_rh" },
    { to: "/users",       label: "Mes intervenants",    icon: Users,           testid: "nav-users",       show: MANAGER_ROLES.includes(role),      section: "rh" },
    { to: "/interimaires", label: "Mes intérimaires",   icon: UserCheck,       testid: "nav-interims",    show: SITES_ROLES.includes(role) && !MANAGER_ROLES.includes(role), section: "rh", module: "module_pointage" },
    // ── Maintenance ──
    { to: "/maintenance",        label: "Maintenance",             icon: Building2,   testid: "nav-maintenance-space", show: SITES_ROLES.includes(role), section: "maintenance", module: "module_maintenance" },
  ];
  return items.filter((i) => i.show);
}

// ── Item de navigation (factorisé) ──────────────────────────────────────────
function NavItem({ item, onNavigate, badge, collapsed }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      onClick={onNavigate}
      title={item.label}
      className={`group relative flex items-center rounded-md mx-2 text-sm font-medium transition-colors duration-150 ${collapsed ? "justify-center gap-0" : "gap-3"}`}
      style={({ isActive }) => ({
        padding: "7px 12px",
        backgroundColor: isActive ? S.activeBg : "transparent",
        color: isActive ? S.activeText : S.inactiveText,
      })}
      onMouseEnter={(e) => {
        if (!e.currentTarget.getAttribute("aria-current")) {
          e.currentTarget.style.backgroundColor = S.hoverBg;
          e.currentTarget.style.color = "var(--cf-ink-2)";
        }
      }}
      onMouseLeave={(e) => {
        if (!e.currentTarget.getAttribute("aria-current")) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = S.inactiveText;
        }
      }}
    >
      {({ isActive }) => (
        <>
          <span className="relative shrink-0">
            <Icon className="h-4 w-4" style={{ color: isActive ? "var(--cf-blue)" : "inherit" }} />
            {collapsed && badge > 0 && (
              <span
                className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                style={{ backgroundColor: item.to.startsWith("/demandes") ? "#f97316" : S.badgeBg }}
              />
            )}
          </span>
          {!collapsed && <span className="truncate flex-1">{item.label}</span>}
          {/* BUG-023 — le compteur était en blanc sur ses deux fonds : 2,8:1
              sur l'orange des demandes, 1,36:1 sur le lime par défaut, à 10 px.
              Les deux fonds sont clairs ; c'est l'encre qu'il leur faut, et
              elle passe largement (7,03:1 et 14,5:1). Encre écrite en dur, pas
              `var(--cf-ink)` : ce token s'éclaircit en thème sombre alors que
              le fond du badge, lui, reste clair — le défaut reviendrait à
              l'identique de nuit. */}
          {!collapsed && badge > 0 && (
            <span
              className="ml-auto text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 inline-flex items-center justify-center"
              style={{
                backgroundColor: item.to.startsWith("/demandes") ? "#f97316" : S.badgeBg,
                color: "#0A0B0C",
              }}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

// ── Section rétractable ──────────────────────────────────────────────────────
function NavSection({ id, label, children }) {
  const storageKey = `nav-sec-${id}`;
  const [open, setOpen] = useState(() => {
    const v = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
    return v === null ? true : v === "1";
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* quota */ }
  };
  return (
    <div className="mt-3 first:mt-1">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-1.5 cf-nav-section-label hover:opacity-80 transition-opacity"
      >
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

// ── Composant ───────────────────────────────────────────────────────────────
export default function Sidebar({ company, user, onNavigate, collapsed = false, onToggleCollapse }) {
  const role = user?.role || "owner";
  const { modules, isLoading: modulesLoading } = useModules();
  const moduleOk = (m) => !m || modulesLoading || modules[m];

  // Items principaux filtrés (rôle + module + mise en sommeil).
  // `nav_visible` liste les entrées endormies qu'une entreprise a rallumées.
  const rallumees = company?.nav_visible || [];
  const enSommeil = (i) =>
    i.sommeil && EN_SOMMEIL_PAR_DEFAUT.includes(i.sommeil) && !rallumees.includes(i.sommeil);
  const navItems = buildNav(role).filter((i) => moduleOk(i.module) && !enSommeil(i));

  // Rapports d'intervention : section Maintenance seulement hors univers —
  // dans l'espace Interventions ils ont déjà leur onglet.
  if (!UNIVERSES_ENABLED && moduleOk("module_maintenance") && role !== "client") {
    navItems.push({ to: "/rapports-intervention", label: "Rapports", icon: FileText, testid: "nav-rapports", section: "maintenance" });
  }
  // Demandes → remontées dans Pilotage (16/08) : création pour
  // worker/foreman/manager, tableau de bord pour l'owner.
  if (CREATION_ROLES.includes(role)) {
    navItems.push({ to: "/demandes", label: "Demandes", icon: ClipboardList, testid: "nav-creation--demandes", section: "pilotage", end: true });
  }
  if (role === "owner") {
    navItems.push({ to: "/demandes/dashboard", label: "Demandes", icon: ClipboardList, testid: "nav-demandes-dashboard", section: "pilotage" });
  }

  const isManager = MANAGER_ROLES.includes(role);
  const isOwner   = role === "owner";
  const showPlanChip = can(user, "sidebar:plan_chip");

  const { data: pendingAbs } = useQuery({
    queryKey: ["absences", { status: "pending" }],
    queryFn:  async () => (await api.get("/absences", { params: { status: "pending" } })).data,
    enabled:  isManager,
    refetchInterval: 60000,
  });
  const pendingCount = pendingAbs?.total || 0;

  const { data: demandesStats } = useQuery({
    queryKey: ["demandes-stats"],
    queryFn:  async () => (await api.get("/demandes/stats")).data,
    enabled:  isOwner || role === "manager",
    refetchInterval: 60000,
  });
  const demandesPending = demandesStats?.pending || 0;

  const badgeFor = (item) => {
    if (item.to === "/absences" && isManager) return pendingCount;
    if (item.to === "/demandes/dashboard") return demandesPending;
    return 0;
  };

  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className="h-full shrink-0 flex flex-col transition-[width] duration-200"
      style={{ width: collapsed ? "72px" : "var(--cf-sidebar-w, 240px)", backgroundColor: S.bg, borderRight: "1px solid var(--cf-border)" }}
    >
      {/* Header logo (+ bouton repli desktop) */}
      <div
        className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-5"} py-4`}
        /* Socle 2026-08 : en tiroir mobile, le wordmark passait sous la
           Dynamic Island — l'inset haut protège le logo. */
        style={{ backgroundColor: S.bgLight, borderBottom: `1px solid ${S.separator}`,
          minHeight: "var(--header-height, 64px)",
          paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}
      >
        <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: "var(--cf-ink)" }}>
          <LogoIcon className="h-5 w-5" style={{ color: "var(--cf-surface)" }} />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="cf-wordmark text-sm leading-tight" style={{ color: "var(--cf-ink)" }}>KNOWROK</p>
            <p data-testid="sidebar-company-name" className="text-xs truncate" style={{ color: S.inactiveText }}>
              {company?.name || "—"}
            </p>
          </div>
        )}
        {onToggleCollapse && !collapsed && (
          <button
            type="button"
            onClick={onToggleCollapse}
            data-testid="sidebar-collapse-btn"
            title="Réduire le menu"
            aria-label="Réduire le menu"
            className="shrink-0 h-7 w-7 grid place-items-center rounded-md hover:bg-[var(--cf-bg)]"
            style={{ color: S.inactiveText }}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Bouton déplier (mode rétracté) */}
      {onToggleCollapse && collapsed && (
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="sidebar-expand-btn"
          title="Déployer le menu"
          aria-label="Déployer le menu"
          className="mx-auto mt-2 h-7 w-7 grid place-items-center rounded-md hover:bg-[var(--cf-bg)]"
          style={{ color: S.inactiveText }}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto overflow-x-hidden">
        {SECTIONS.map((sec) => {
          const secItems = navItems.filter((i) => i.section === sec.key);
          if (secItems.length === 0) return null;
          if (collapsed) {
            return (
              <div key={sec.key} className="py-1.5 first:pt-0 space-y-0.5" style={{ borderBottom: `1px solid ${S.separator}` }}>
                {secItems.map((item) => (
                  <NavItem key={item.to} item={item} onNavigate={onNavigate} badge={badgeFor(item)} collapsed />
                ))}
              </div>
            );
          }
          return (
            <NavSection key={sec.key} id={sec.key} label={sec.label}>
              {secItems.map((item) => (
                <NavItem key={item.to} item={item} onNavigate={onNavigate} badge={badgeFor(item)} />
              ))}
            </NavSection>
          );
        })}
      </nav>

      {/* Pied : plan (owner/manager), masqué en mode rétracté */}
      {showPlanChip && !collapsed && (
        <div data-testid="sidebar-plan-chip" className="px-4 py-3" style={{ borderTop: `1px solid ${S.separator}` }}>
          <p className="text-xs" style={{ color: S.inactiveText }}>
            Plan <span className="font-semibold" style={{ color: "var(--cf-ink)" }}>{company?.plan || "—"}</span>
          </p>
        </div>
      )}
    </aside>
  );
}
